/**
 * The touchdown dance, as a loop of three moves: a groove (twist and sway on the beat, fists pumping in
 * turn, a bounce on every beat), a spin with the arms out, then a flex (arms curled, leaning back). Pure
 * timing maths, so it is the same on every device and can be tested; the athlete view turns it into a pose.
 */

export type DanceArm = 'pumpUp' | 'pumpDown' | 'out' | 'flex';

export interface DancePose {
  /** Turn added to the body's facing, radians. */
  yaw: number;
  /** Side-to-side sway, radians. */
  roll: number;
  /** Lean, radians (negative leans back). */
  pitch: number;
  /** Feet off the turf, yards. */
  lift: number;
  left: DanceArm;
  right: DanceArm;
}

export interface DanceTimes {
  grooveSeconds: number;
  spinSeconds: number;
  flexSeconds: number;
  beatHz: number;
  twistRad: number;
  swayRad: number;
  bounceYards: number;
  spinHopYards: number;
  flexLeanRad: number;
}

/** Eased 0 to 1 across [a, b]. */
function ease(x: number, a: number, b: number): number {
  const k = Math.min(1, Math.max(0, (x - a) / (b - a)));
  return k * k * (3 - 2 * k);
}

export function danceAt(time: number, d: DanceTimes): DancePose {
  const loop = d.grooveSeconds + d.spinSeconds + d.flexSeconds;
  const t = ((time % loop) + loop) % loop;
  const beat = 2 * Math.PI * d.beatHz;
  if (t < d.grooveSeconds) {
    const s = Math.sin(t * beat);
    // In from standing over the first beat, so the dance does not start with a jolt.
    const k = time < loop ? ease(t, 0, 1 / d.beatHz / 2) : 1;
    return {
      yaw: d.twistRad * s * k,
      roll: d.swayRad * s * k,
      pitch: 0,
      lift: d.bounceYards * Math.abs(Math.sin(t * beat)),
      left: s > 0 ? 'pumpUp' : 'pumpDown',
      right: s > 0 ? 'pumpDown' : 'pumpUp',
    };
  }
  if (t < d.grooveSeconds + d.spinSeconds) {
    const k = ease(t, d.grooveSeconds, d.grooveSeconds + d.spinSeconds);
    return {
      yaw: 2 * Math.PI * k,
      roll: 0,
      pitch: 0,
      lift: d.spinHopYards * Math.sin(k * Math.PI),
      left: 'out',
      right: 'out',
    };
  }
  const f = t - d.grooveSeconds - d.spinSeconds;
  const lean = ease(f, 0, d.flexSeconds * 0.3) * (1 - ease(f, d.flexSeconds * 0.8, d.flexSeconds));
  return {
    yaw: 0,
    roll: 0,
    pitch: d.flexLeanRad * lean,
    lift: d.bounceYards * 0.5 * Math.abs(Math.sin(f * beat)),
    left: 'flex',
    right: 'flex',
  };
}
